<template>
  <Modal ref="modalRef" width="27.5rem" @after-close="reset">
    <template #header>
      <Text variant="h4">{{ step === 'confirm' ? 'Verify your phone number' : 'Enter the code' }}</Text>
    </template>

    <template #body>
      <View v-if="step === 'confirm'" direction="column" gap="sm">
        <Text variant="body-medium" color="secondary">
          We'll text a 6-digit code to
          <b>{{ formattedPhone }}</b>.
        </Text>
        <!-- Carriers read this line when they vet Tallo's 10DLC texting campaign: keep every disclosure in it -->
        <Text class="consent" variant="body-small" color="tertiary">
          By verifying, you agree that Tallo may text you about your account, listings and tours, including with
          automated messages. Message frequency varies. Message and data rates may apply. Reply HELP for help or STOP
          to opt out. See our
          <Link color="accent" :to="privacyAndPolicyLink" target="_blank">Privacy Policy</Link>
        </Text>
      </View>

      <View v-else direction="column" gap="md">
        <Text variant="body-medium" color="secondary">Sent to {{ formattedPhone }}</Text>
        <CodeInput ref="codeInputRef" v-model="code" @complete="verify" />
        <Text v-if="codeError" variant="body-small" class="code-error">{{ codeError }}</Text>
        <Text variant="body-medium" color="tertiary">
          Didn't get it?
          <Link v-if="resendSecondsLeft === 0" color="accent" @click="sendCode">Resend</Link>
          <b v-else>Resend in 0:{{ String(resendSecondsLeft).padStart(2, '0') }}</b>
        </Text>
      </View>
    </template>

    <template #footer>
      <Button variant="ghost" size="medium" @click="close">Cancel</Button>
      <Button v-if="step === 'confirm'" size="medium" :loading="isSending" @click="sendCode">Send code</Button>
      <Button v-else size="medium" :disabled="code.length < 6" :loading="isVerifying" @click="verify">Verify</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref } from 'vue';

import { Button, CodeInput, extractErrorMessage, Link, Modal, Text, useAlertsStore, View } from '@tallo/design-system';
import { profileService, useInvestorStore } from '@tallo/investor/investor';
import { privacyAndPolicyLink } from '@tallo/navigation';
import { formatPhoneNumber } from '@tallo/utility';

const props = defineProps<{ phoneNumber: string }>();
const emit = defineEmits<{ verified: []; closed: [] }>();

const investorStore = useInvestorStore();
const alertsStore = useAlertsStore();

const modalRef = ref<InstanceType<typeof Modal>>();
const codeInputRef = ref<InstanceType<typeof CodeInput>>();
const step = ref<'confirm' | 'code'>('confirm');
const code = ref('');
const codeError = ref('');
const isSending = ref(false);
const isVerifying = ref(false);
const resendSecondsLeft = ref(0);
let resendTimer: ReturnType<typeof setInterval> | undefined;

const formattedPhone = computed(() => formatPhoneNumber(props.phoneNumber));

function show() {
  step.value = 'confirm';
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

function reset() {
  step.value = 'confirm';
  code.value = '';
  codeError.value = '';
  stopResendCountdown();
  emit('closed');
}

async function sendCode() {
  isSending.value = true;
  codeError.value = '';

  try {
    await profileService.sendPhoneVerificationCode();
    step.value = 'code';
    code.value = '';
    startResendCountdown();
    await nextTick();
    codeInputRef.value?.focus();
  } catch (error) {
    alertsStore.error(extractErrorMessage(error, 'Could not send the code. Please try again.'));
  } finally {
    isSending.value = false;
  }
}

async function verify() {
  if (code.value.length < 6 || isVerifying.value) return;

  isVerifying.value = true;
  codeError.value = '';

  try {
    const user = await profileService.verifyPhone(code.value);
    investorStore.setUser(user);
    alertsStore.success('Phone number verified');
    emit('verified');
    close();
  } catch (error) {
    codeError.value = extractErrorMessage(error, 'Invalid code. Please try again.');
    code.value = '';
    codeInputRef.value?.focus();
  } finally {
    isVerifying.value = false;
  }
}

// Mirrors the backend's 60s resend cooldown so the link appears right when a resend can succeed
function startResendCountdown() {
  resendSecondsLeft.value = 60;
  stopResendCountdown();
  resendTimer = setInterval(() => {
    resendSecondsLeft.value -= 1;
    if (resendSecondsLeft.value <= 0) stopResendCountdown();
  }, 1000);
}

function stopResendCountdown() {
  if (resendTimer) {
    clearInterval(resendTimer);
    resendTimer = undefined;
  }
}

onUnmounted(stopResendCountdown);

defineExpose({ show });
</script>

<style scoped lang="scss">
.code-error {
  color: var(--t-text-color-danger-primary);
}

.consent a {
  font-weight: var(--t-font-weight-medium);
}
</style>
